import { readTableView, writeTableView, type TableView, type TableViewSpec } from "_shared/_views/list-pages/table-view-params";
import type { ListChipSelection } from "_shared/_views/list-pages/list-chip-selection";
import type { MRT_ColumnFiltersState, MRT_SortingState, MRT_Updater } from "material-react-table";
import { useMemo } from "react";
import { useSearchParams } from "react-router";

/**
 * Whether two sets of parameters say the same thing. Writing a view puts its parameters back in a
 * fixed order, so a change that changes nothing can still reorder them; that is not a change.
 */
function isSameView(left: URLSearchParams, right: URLSearchParams): boolean {
  const sorted = (params: URLSearchParams) => [...params].map(([name, value]) => `${name}=${value}`).sort().join("&");
  return sorted(left) === sorted(right);
}

function applyUpdater<T>(updater: MRT_Updater<T>, previous: T): T {
  return typeof updater === "function" ? (updater as (previous: T) => T)(previous) : updater;
}

export type TableViewParamsOptions = Readonly<{
  /**
   * Told the parameters each change the reader makes through the setters is about to write, before
   * the URL has them: what `useCurrentTableView`'s `remember` keeps (#6714). Never called for a URL
   * the reader arrived at.
   */
  onReaderChange?: (next: URLSearchParams) => void;
}>;

/**
 * A list page's view -- chips, column filters, sort and page size -- read from its URL and written
 * back to it (#6712), so a reload, a shared link or the Back button shows the list the reader saw.
 *
 * The setters take MRT's updaters, so a page hands them to the table as `onColumnFiltersChange`,
 * `onSortingChange` and the page size of `onPaginationChange`. React Router hands a functional
 * update the parameters of the last render, so two setters called in one turn keep only the second:
 * make one change per event.
 *
 * `spec` must be stable -- hoist it to module scope, or memoize it on the tab on screen -- because the
 * view is read again only when it or the URL changes. The view is a stable value on purpose: a page
 * builds its list request from it, and a new array on every render would send that request again.
 */
export function useTableViewParams(spec: TableViewSpec, options?: TableViewParamsOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const onReaderChange = options?.onReaderChange;
  const view = useMemo(() => readTableView(spec, searchParams), [spec, searchParams]);

  function update(change: (current: TableView) => Partial<TableView>) {
    setSearchParams((current) => {
      const now = readTableView(spec, current);
      const next = writeTableView(spec, { ...now, ...change(now) }, current);
      if (isSameView(next, current)) return current;
      onReaderChange?.(next);
      return next;
    });
  }

  return {
    ...view,
    setChips: (chips: ListChipSelection) => update(() => ({ chips })),
    setColumnFilters: (updater: MRT_Updater<MRT_ColumnFiltersState>) =>
      update((current) => ({ columnFilters: applyUpdater(updater, current.columnFilters) })),
    setSorting: (updater: MRT_Updater<MRT_SortingState>) =>
      update((current) => ({ sorting: applyUpdater(updater, current.sorting) })),
    setPageSize: (pageSize: number) => update(() => ({ pageSize })),
  };
}
